// Local
import { UserTypeEnum } from '@app/model/user-types.model';

// The user types that have bar numbers
export const userTypesThatHaveBarNumbers: string[] = [
  UserTypeEnum.Attorney,
  UserTypeEnum.Judge,
  UserTypeEnum.NonSubscribingJudgeUser,
  UserTypeEnum.NonSubscribingUser,
  UserTypeEnum.Clerk
];

export const filingOptionMap: Record<number, string> = {
  0: 'File And Serve',
  1: 'File Only',
  2: 'Serve Only - Private',
  3: 'Serve Only - Public',
  4: 'Document Upload'
};

/**
 * Enum for document rendering options.
 */
export enum DOCUMENT_RENDERING {
  ORIGINAL = 'original',
  CONVERTED_PDF = 'converted-pdf',
  CLERK_STAMPED = 'clerk-stamped-pdf',
  /** Working-copy base rendering — the filer's PDF copied on judge-review open. Always present. */
  ORIGINAL_FILER_PDF = 'original-filer-pdf',
  /** Preview rendering produced when the judge uploads a Replace document. */
  JUDGE_REVIEW_REPLACE = 'judge-review-replace',
  /** Preview rendering produced when the judge uploads an Attach document. */
  JUDGE_REVIEW_ATTACH = 'judge-review-attach',
}

export enum ENTITY_TYPE {
  Individual = 0,
  Organization = 1
}

/**
 * Cap for individual name fields (First, Middle, Last), in pixels, bound to `fsx-input`'s
 * `maxWidth` input.
 *
 * `fsx-input` has no width or `flex` of its own, so inside the flex `.form-row` from
 * `forms.scss` it is sized by its widest content — including its validation message. The
 * library's wording for `minlength` ("First Name does not contain enough characters") measures
 * wider than the field, so First Name stretched past its neighbours the moment the 2-character
 * minimum tripped (Bug 172043). Capping the host makes the message wrap inside the field instead.
 *
 * **Bind this only where no stylesheet already caps the field.** `sidecars.scss` caps every
 * `fsx-input` inside a `.panel-body` at 180px, which contains the widening on its own, and
 * `maxWidth` writes an *inline* style that outranks that rule — so binding it there would replace
 * a CSS-owned width with a TS-owned one for no gain, and would silently stay at this value if the
 * sidecar rule ever changes.
 *
 * That makes it a property of the **host**, not of the form component: a form rendered in two
 * places can need the cap in one and not the other. So a form with more than one host takes it as
 * an `@Input` and each host decides. Current wiring:
 *
 * - new-case `edit-party-content` — reads this constant directly; its only host chain
 *   (`parties.component` → `edit-party-container`) has no panel ancestor.
 * - new-case `add-party-form` — takes `[nameFieldMaxWidth]`. Set by the parties-page
 *   `mat-expansion-panel` host; left unset by the Add Party sidecar host, which `sidecars.scss`
 *   already caps.
 *
 * The alias panels and the shared add-party-sidecar name form are covered by `sidecars.scss` and
 * bind nothing. Grid-based name rows such as `add-service-party-sidecar` need nothing either —
 * their tracks already bound the field.
 *
 * Shared rather than inlined (the app's other `[maxWidth]` bindings are literals) because these
 * two sites must agree with each other and with the 180px in `sidecars.scss`: a party looks the
 * same whether you add it or edit it, and drift between them is the bug this fixes. Keep the
 * value equal to the `sidecars.scss` cap.
 *
 * Suffix is excluded — it carries `.small-input { max-width: 90px }` at both sites, and an inline
 * cap would widen it to 180.
 */
export const NAME_FIELD_MAX_WIDTH = 180;

/** Snackbar shown when an attorney search request fails, in filing and in Case & Party Management. */
export const ATTORNEY_SEARCH_FAILED =
  'The attorney search could not be completed. Please try again.';
